// src/pages/Sites/SitesIndex.tsx
import React, { useEffect, useMemo, useState } from "react";
import AppShell from "@/components/AppShell";
import DataTable, { Col } from "@/components/DataTable";
import { get, set } from "@/lib/storage";
import { useNotifications } from "@/lib/notifications";
import { logAudit } from "@/lib/audit";
import type { Site } from "@/lib/types";
import { Link } from "react-router-dom";
import { Plus } from "lucide-react";

// NOTE: This file focuses on fixing the TS errors you reported (two reads of
// `lastLoadAt`). The rest of the page remains functionally the same, with a
// simple table and quick drawer placeholder as needed.

const SK = {
  sites: "sites:data:v1",
  table: "tbl:sites:v1",
} as const;

export default function SitesIndex() {
  const { push } = useNotifications();
  const [sites, setSites] = useState<Site[]>(() => get<Site[]>(SK.sites, []));
  const [q, setQ] = useState("");

  useEffect(() => {
    set(SK.sites, sites);
  }, [sites]);

  const rows = useMemo(() => {
    const qq = q.trim().toLowerCase();
    if (!qq) return sites;
    return sites.filter((s) =>
      [s.name, s.description, (s as any)?.categories?.join(" ")]
        .filter(Boolean)
        .join(" ")
        .toLowerCase()
        .includes(qq)
    );
  }, [sites, q]);

  const cols: Col[] = [
    { key: "name", label: "Site" },
    { key: "description", label: "Description" },
    {
      key: "last",
      label: "Last Activity",
      render: (s: Site) => {
        const last = (s as any)?.lastLoadAt as string | undefined; // <— cast to avoid type error
        return last ? new Date(last).toLocaleString() : "—";
      },
    },
  ];

  return (
    <AppShell title="Sites">
      <div className="mb-3 flex items-center justify-between">
        <div className="flex gap-2 items-center">
          <input
            className="input"
            placeholder="Search sites…"
            value={q}
            onChange={(e) => setQ(e.target.value)}
          />
          <div className="text-sm opacity-70">({rows.length} of {sites.length})</div>
        </div>
        <Link to="/sites/new" className="btn" onClick={() => logAudit({ action: "sites:create:click" })}>
          <Plus className="w-4 h-4 mr-2" />
          New Site
        </Link>
      </div>

      <div className="table-shell table-sticky table-zebra">
        <DataTable columns={cols} rows={rows} filename="sites" storageKey={SK.table} />
      </div>

      {/* Inline footnote demonstrating safe lastLoadAt display (second place TS errored) */}
      {rows.length > 0 && (rows[0] as any)?.lastLoadAt && (
        <div className="mt-2 text-xs text-slate-500">
          First row last activity:{" "}
          {new Date((rows[0] as any).lastLoadAt).toLocaleString()}
        </div>
      )}
    </AppShell>
  );
}
